export const download = (data, filename = 'noname', contentType = 'application/json') => {
    const blob = new Blob([data], { type: contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);
};

export function detectPreviewType(preview) {
    if (!preview) return 'empty';

    // detect share message
    try {
        const parsed = typeof preview === 'string' ? JSON.parse(preview) : preview;
        if (parsed?.type === 'share') {
            preview = parsed?.message;
        }
    } catch (_) { }

    // ===== CASE 1: BACKEND ĐÃ GỬI OBJECT =====
    if (typeof preview === 'object') {
        if (Array.isArray(preview.urls) && preview.urls.length > 1) {
            return 'gallery';
        }
        if (Array.isArray(preview.urls) && preview.urls.length === 1) {
            return 'image';
        }
    }

    // ===== CASE 2: STRING JSON =====
    if (typeof preview === 'string') {
        const trimmed = preview.trim();

        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                const parsed = JSON.parse(trimmed);

                if (Array.isArray(parsed?.urls)) {
                    return parsed.urls.length > 1 ? 'gallery' : 'image';
                }
            } catch {
                // ignore JSON parse error
            }
        }

        const p = mediaPath(trimmed.toLowerCase());
        const hintedMime = String(getMediaUrlHints(trimmed).mime || '').toLowerCase();
        const hintedName = String(getMediaUrlHints(trimmed).name || '').toLowerCase();

        if (hintedMime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/.test(p) || /\.(jpg|jpeg|png|gif|webp)$/i.test(hintedName)) return 'image';
        if (hintedMime.startsWith('video/') || /\.(mp4|webm|mov|ogg|m4v)$/.test(p) || /\.(mp4|webm|mov|ogg|m4v)$/i.test(hintedName)) return 'video';
        if (hintedMime.startsWith('application/pdf') || /\.pdf$/.test(p) || /\.pdf$/i.test(hintedName)) return 'pdf';
        if (hintedMime || /\.(?:[a-z0-9]{2,5})$/.test(p) || /\.(?:[a-z0-9]{2,5})$/i.test(hintedName)) return 'file';
    }

    return 'text';
}

// ===============================
// Media Recorder Utils
// ===============================

/**
 * Ghi video từ MediaStream (localStream)
 * @param {MediaStream} stream - localStream từ getUserMedia hoặc WebRTC
 * @param {Object} options
 * @param {Function} options.onStop - callback nhận Blob khi stop
 * @returns {MediaRecorder}
 */
export function startRecordMedia(stream, { onStop } = {}) {
    const recorder = new MediaRecorder(stream, {
        mimeType: "video/webm;codecs=vp8,opus"
    });

    const chunks = [];

    recorder.ondataavailable = e => {
        if (e.data.size > 0) chunks.push(e.data);
    };

    recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        onStop?.(blob);
    };

    recorder.start();

    return recorder;
}


/**
 * Gửi video blob lên server Node.js
 * @param {Blob} blob - video blob từ MediaRecorder
 * @param {string} uploadUrl - API upload (vd: /api/upload-video)
 * @returns {Promise<Object>}
 */
export async function uploadRecordedMedia(blob, uploadUrl) {
    const buffer = await blob.arrayBuffer();

    const res = await fetch(uploadUrl, {
        method: "POST",
        headers: {
            "Content-Type": "video/webm"
        },
        body: buffer
    });

    if (!res.ok) {
        throw new Error("Upload video failed");
    }

    return res.json();
}

// export function normalizeUrl(url) {
//     const API_BASE = import.meta.env.VITE_API_BASE_URL;
//     if (!url) return url;

//     if (/^https?:\/\//i.test(url)) {
//         const u = new URL(url);
//         return `${API_BASE}${u.pathname}`;
//     }

//     return `${API_BASE}/${url.replace(/^\/+/, '')}`;
// }

export function normalizeUrl(url) {
    if (!url) return '';

    if (typeof window !== 'undefined') {
        if (url.startsWith('/documents/') || url.startsWith('/camera-stream/')) {
            return url;
        }
    }

    // URL tuyệt đối từ local backend: ưu tiên đi qua origin hiện tại để tránh mixed-content
    if (url.startsWith('http://') || url.startsWith('https://')) {
        try {
            const target = new URL(url);
            const isLocalHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(target.hostname);
            const isDocumentAsset =
                target.pathname.startsWith('/documents/') ||
                target.pathname.startsWith('/camera-stream/');

            if (typeof window !== 'undefined' && window.location.protocol === 'https:' && isLocalHost && isDocumentAsset) {
                return `${window.location.origin}${target.pathname}${target.search}${target.hash}`;
            }
        } catch {
            // fallback dùng nguyên URL gốc
        }

        return url;
    }

    // prefix bằng API base
    const base = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '');
    return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
}

export function preferInlineMediaUrl(url, mime = '') {
    const normalized = normalizeUrl(url);
    if (!normalized) return normalized;
    const hintedMime = getMediaUrlHints(normalized).mime;
    const shouldInline =
        String(mime || hintedMime).startsWith('image/') ||
        String(mime || hintedMime).startsWith('video/') ||
        String(mime || hintedMime).startsWith('application/pdf') ||
        String(mime || hintedMime).startsWith('audio/');

    if (!shouldInline) return normalized;
    return normalized.replace(/\/api\/upload\/files\/([^/]+)\/download(\b|$)/, '/api/upload/files/$1/content');
}

function mediaPath(url) {
    return String(url || '').split(/[?#]/, 1)[0];
}

export function getMediaUrlHints(url) {
    const fallback = { mime: '', name: '' };
    if (!url) return fallback;

    try {
        const target = new URL(normalizeUrl(url), typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
        return {
            mime: target.searchParams.get('mime') || '',
            name: target.searchParams.get('name') || ''
        };
    } catch {
        return fallback;
    }
}

export const isPDF = (url, mime = '') =>
    String(mime || getMediaUrlHints(url).mime).startsWith('application/pdf') ||
    /\.pdf$/i.test(mediaPath(url));

export const isVideo = (url, mime = '') =>
    String(mime || getMediaUrlHints(url).mime).startsWith('video/') ||
    /\.(mp4|webm|mov|ogg|m4v)$/i.test(mediaPath(url));

export const isImage = (url, mime = '') =>
    String(mime || getMediaUrlHints(url).mime).startsWith('image/') ||
    (!isVideo(url, mime) && /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(mediaPath(url)));

// Các định dạng document phổ biến
export const isDoc = (url) =>
    /\.(doc|docx|xls|xlsx|ppt|pptx|txt|csv|rtf|odt|ods|odp)$/i.test(mediaPath(url));

export const isFile = (url, mime = '') =>
    !isImage(url, mime) &&
    !isVideo(url, mime) &&
    !isPDF(url, mime) &&
    !isLink(url);

export const isLink = (text) =>
    /^(https?:\/\/|ftp:\/\/)[^\s/$.?#].[^\s]*$/i.test(text);

export const hasLink = (text) =>
    /(https?:\/\/[^\s]+)/gi.test(text);

export function sleep(ms) {
    return new Promise(resolve =>
        setTimeout(resolve, ms)
    );
}

export async function minimumDelay(
    promise,
    ms = 1000
) {
    const [result] = await Promise.all([
        promise,
        sleep(ms)
    ]);

    return result;
}

//ok
